import { CLASS_NAME } from 'constants/class-names'
import { EventType, HtmlAttr, SlidingDrawerEvent } from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { dispatchAnalyticsFindVehicleSubmitEvent } from 'utils/analytics'
import { dispatchAnalyticsClickToSectionEvent } from 'utils/analytics'
import { ComponentFactory } from 'services/component-factory'

const { vehicleFinder: className } = CLASS_NAME

export class VehicleFinderFormComponent extends ComponentBase {
    public static pageUrl = '/find-compare/'

    private form: HTMLFormElement

    public constructor(form: HTMLFormElement) {
        super()

        this.form = form

        document.body.addEventListener('htmx:afterSwap', () => {
            new ComponentFactory('main', 'dropdownChecklist')
            new ComponentFactory('main', 'carousel')
            this.initButtons()
            this.initPriceInputValidation()
        })

        this.initPriceInputValidation()
        this.disableFocusAdvancedFilterElements()

        window.addEventListener(SlidingDrawerEvent.CLOSED, () => {
            this.disableFocusAdvancedFilterElements()

            const searchParams = new URL(document.location.toString())
                .searchParams
            if (searchParams.get('search')) {
                return
            }

            const data = window.htmx.values(
                window.htmx.find('#primary-vehicle-finder-form')
            )
            data['htmx'] = 1

            window.htmx.ajax('GET', VehicleFinderFormComponent.pageUrl, {
                target: '#primary-vehicle-finder-form',
                select: '#primary-vehicle-finder-form',
                headers: { 'Hx-Template': 'primary-vehicle-finder-form.html' },
                swap: 'outerHTML',
                values: data
            })
        })

        const sortInput = this.getElement<HTMLInputElement>('[name="sort"]')
        if (sortInput) {
            sortInput.setAttribute(HtmlAttr.TAB_INDEX, '-1')
            sortInput.setAttribute('aria-hidden', 'true')
        }

        this.initButtons()
    }

    /**
     * Button click behaviour
     * submit button, advanced filter button
     */
    private initButtons(): void {
        this.submitButtons.forEach((btn) => {
            btn.addEventListener(EventType.CLICK, (e) => {
                e.preventDefault()
                this.submitForm()
            })
        })

        if (this.filterButton) {
            this.filterButton.addEventListener(EventType.CLICK, () => {
                this.showAdvFilter()
                dispatchAnalyticsClickToSectionEvent(
                    'Vehicle-Finder',
                    'Advanced-Filter'
                )
            })
        }
    }

    /**
     * Actions when the advanced filter button clicked
     */
    private showAdvFilter(): void {
        this.enableFocusAdvancedFilterElements()
        if (this.filterButton) {
            const id = this.filterButton.dataset.targetId
            window.dispatchEvent(
                new CustomEvent(SlidingDrawerEvent.OPEN, {
                    detail: id
                })
            )
        }
    }

    /**
     * on every focusable element add tabIndex = -1
     * */
    private disableFocusAdvancedFilterElements(): void {
        if (!this.advancedFilterSection) {
            return
        }

        const focusableElements = [
            ...this.advancedFilterSection.querySelectorAll(
                'a[href], button, input, [tabindex]:not([tabindex="-1"])'
            )
        ]
        focusableElements.map((el) => el.setAttribute(HtmlAttr.TAB_INDEX, '-1'))
        this.advancedFilterSection.setAttribute('aria-hidden', 'true')
    }

    /**
     * on every focusable element add tabIndex = 0, set the focus on the close button
     * */
    private enableFocusAdvancedFilterElements(): void {
        if (!this.advancedFilterSection) {
            return
        }

        const focusableElements = [
            ...this.advancedFilterSection.querySelectorAll(
                'a[href], button, input'
            )
        ]
        focusableElements.map((el) => el.setAttribute(HtmlAttr.TAB_INDEX, '0'))
        this.advancedFilterSection.setAttribute('aria-hidden', 'false')
        this.advancedFilterSection.focus()
    }

    /**
     * navigate to the find-compare page with query params
     *
     * no-js fallback: form order, type GET (no comma separated checkboxes)
     */
    private submitForm(): void {
        this.validatePriceRange()

        const form = this.getElement<HTMLFormElement>(`#${this.form.id}`)
        if (form && !form.reportValidity()) {
            return
        }

        this.disableSubmitButtons()
        dispatchAnalyticsFindVehicleSubmitEvent(
            this.makes,
            this.models,
            this.bodystyles,
            this.minPrice,
            this.maxPrice,
            this.years,
            this.seats,
            this.powerSources,
            this.transmissions,
            this.sort
        )
        if (form) {
            form.submit()
        }
    }

    private disableSubmitButtons(): void {
        this.submitButtons.forEach((btn) => {
            btn.disabled = true
        })
    }

    /**
     * Prevent invalid characters in price input fields
     * HTML number inputs allow e, E, +, ., - which are not valid for price
     */
    private initPriceInputValidation(): void {
        const priceSelectors = [
            '[name="price_min"]',
            '[name="price_max"]',
            '[name="price_min_copy"]',
            '[name="price_max_copy"]'
        ]
        const invalidKeys = ['e', 'E', '+', '.', '-']

        priceSelectors.forEach((selector) => {
            const input = this.getElement<HTMLInputElement>(selector)
            if (!input) {
                return
            }

            input.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
                if (invalidKeys.includes(e.key)) {
                    e.preventDefault()
                }
            })

            input.addEventListener('paste', (e: ClipboardEvent) => {
                e.preventDefault()
                const pasted = e.clipboardData?.getData('text') ?? ''
                const digitsOnly = pasted.replace(/\D/g, '')
                const sanitized = digitsOnly.replace(/^0+(\d)/, '$1')
                input.value = sanitized
                input.dispatchEvent(new Event('input', { bubbles: true }))
            })

            input.addEventListener('input', () => {
                input.value = input.value.replace(/^0+(\d)/, '$1')
                this.validatePriceRange()
            })
        })
    }

    /**
     * Validate that min price is less than max price
     * Uses setCustomValidity for native browser error display
     */
    private validatePriceRange(): void {
        const pairs = [
            { min: '[name="price_min"]', max: '[name="price_max"]' },
            { min: '[name="price_min_copy"]', max: '[name="price_max_copy"]' }
        ]

        pairs.forEach(({ min, max }) => {
            const minInput = this.getElement<HTMLInputElement>(min)
            const maxInput = this.getElement<HTMLInputElement>(max)
            if (!minInput || !maxInput) {
                return
            }

            const minVal = parseInt(minInput.value, 10)
            const maxVal = parseInt(maxInput.value, 10)

            if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
                minInput.setCustomValidity(
                    'Please enter a smaller value than maximum price'
                )
                maxInput.setCustomValidity(
                    'Please enter a larger value than minimum price'
                )
            } else {
                minInput.setCustomValidity('')
                maxInput.setCustomValidity('')
            }
        })
    }

    /** Element Properties */

    private get filterButton(): Nullable<HTMLButtonElement> {
        return this.getElement<HTMLButtonElement>(`.${className.filterToggle}`)
    }

    private get submitButtons(): HTMLButtonElement[] {
        return this.getElements<HTMLButtonElement>(
            '.vehicle-finder--submit-button'
        )
    }

    private get modelCheckboxes(): HTMLInputElement[] {
        const modelFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.modelFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            modelFieldset
        )
    }

    private get advancedFilterSection(): Nullable<HTMLElement> {
        return this.getElement<HTMLElement>(`.${className.advancedFormSection}`)
    }

    /** Value Properties */

    public get models(): Array<string> {
        return this.modelCheckboxes
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get makes(): Array<string> {
        const makeFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.makeFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            makeFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get bodystyles(): Array<string> {
        const bodystylesFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.bodystylesFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            bodystylesFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get seats(): Array<string> {
        const seatsFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.seatsFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            seatsFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get years(): Array<string> {
        const yearsFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.yearsFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            yearsFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get transmissions(): Array<string> {
        const transmissionsFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.transmissionsFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            transmissionsFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get powerSources(): Array<string> {
        const powerSourcesFieldset = this.getElement<HTMLFieldSetElement>(
            `.${className.powerSourcesFieldset}`
        )
        return this.getElements<HTMLInputElement>(
            '[type="checkbox"]',
            powerSourcesFieldset
        )
            .filter((checkbox) => checkbox.checked === true)
            .map((filtered) => filtered.value)
    }

    public get minPrice(): string {
        const minPriceInput =
            this.getElement<HTMLInputElement>('[name="price_min"]')
        if (minPriceInput) {
            return minPriceInput.value
        }
        return ''
    }

    public get maxPrice(): string {
        const maxPriceInput =
            this.getElement<HTMLInputElement>('[name="price_max"]')
        if (maxPriceInput) {
            return maxPriceInput.value
        }
        return ''
    }

    public get sort(): string {
        const sortInput = this.getElement<HTMLInputElement>('[name="sort"]')
        if (sortInput) {
            return sortInput.value
        }
        return ''
    }
}
